/**
 * transformHistory — undo/redo for gizmo transforms.
 *
 * Mirrors materialHistory.js (module-level, survives panel unmount, keyed per
 * subject) with one simplification: a gizmo has a real gesture boundary, so
 * this needs no coalescing window. materialHistory has to guess with a 1s timer
 * because a slider fires hundreds of change events with nothing marking "the
 * drag started"; TransformControls emits `dragging-changed`, so one drag is
 * exactly one entry, deterministically.
 *
 * Entries are transform snapshots — { position, rotation, scale } as read from
 * the adapter — not store patches. That keeps the stack adapter-agnostic: the
 * spotlight's cylindrical mapping (slice 3) undoes through the same code
 * without this module knowing anything about it.
 */

const MAX_DEPTH = 50

// Gestureless edits (a typed field, a slider drag) have no press/release to
// bracket them, so consecutive edits to the SAME field within this window
// collapse into one undo step — typing "1.61" is one Ctrl+Z, not four. Same
// heuristic (and duration) as materialHistory, used only where a real gesture
// boundary isn't available.
const COALESCE_MS = 1000

// adapterId -> { stack: Transform[], redo: Transform[], lastKey, lastTime }
const _byId = new Map()
const _listeners = new Set()

// Which subject was edited most recently. Lets Ctrl+Z target the right object
// when nothing is selected in the outliner — editing the pose fields in the
// tune panel doesn't require a selection, so undo must not require one either.
let _lastEditedId = null

function _notify() {
  for (const fn of _listeners) { try { fn() } catch { /* a bad listener must not break undo */ } }
}

function _get(id) {
  let h = _byId.get(id)
  if (!h) { h = { stack: [], redo: [], lastKey: null, lastTime: 0 }; _byId.set(id, h) }
  return h
}

function _clone(t) {
  return {
    position: [...t.position],
    rotation: [...t.rotation],
    scale: [...t.scale],
  }
}

/** True when two snapshots are identical to within float noise. */
export function sameTransform(a, b, eps = 1e-9) {
  if (!a || !b) return false
  for (const k of ['position', 'rotation', 'scale']) {
    for (let i = 0; i < 3; i++) {
      if (Math.abs((a[k]?.[i] ?? 0) - (b[k]?.[i] ?? 0)) > eps) return false
    }
  }
  return true
}

/**
 * Push the transform as it was BEFORE a gesture. Call once on drag start.
 * A no-op gesture (press and release without moving) is pruned by commit().
 */
export function begin(id, before) {
  if (!id || !before) return
  const h = _get(id)
  h.stack.push(_clone(before))
  if (h.stack.length > MAX_DEPTH) h.stack.shift()
  h.redo = [] // a new edit invalidates the redo branch
  // A gesture is its own boundary — never let the next field edit coalesce
  // into it just because it happened within the window.
  h.lastKey = null
  h.lastTime = 0
  _lastEditedId = id
  _notify()
}

/**
 * Record a snapshot taken immediately BEFORE a gestureless edit to field `key`
 * (a typed pose field, a slider drag). Consecutive same-key edits inside
 * COALESCE_MS extend the existing entry instead of pushing a new one.
 *
 * `now` is injectable so the coalescing window is testable without faking time.
 */
export function recordEdit(id, before, key, now = Date.now()) {
  if (!id || !before) return
  const h = _get(id)
  const continuing = h.lastKey === key && (now - h.lastTime) < COALESCE_MS && h.stack.length > 0
  if (!continuing) {
    h.stack.push(_clone(before))
    if (h.stack.length > MAX_DEPTH) h.stack.shift()
    h.redo = []
  }
  h.lastKey = key
  h.lastTime = now
  _lastEditedId = id
  _notify()
}

/** The subject edited most recently, or null. */
export function lastEditedId() { return _lastEditedId }

/**
 * Call on drag end with the resulting transform. If nothing actually moved,
 * the entry pushed by begin() is dropped so a stray click doesn't cost the
 * user an undo step.
 */
export function commit(id, after) {
  const h = _byId.get(id)
  if (!h || h.stack.length === 0) return
  const before = h.stack[h.stack.length - 1]
  if (sameTransform(before, after)) {
    h.stack.pop()
    _notify()
  }
}

/** Returns the snapshot to restore, or null. `current` goes onto the redo stack. */
export function undo(id, current) {
  const h = _byId.get(id)
  if (!h || h.stack.length === 0) return null
  const restore = h.stack.pop()
  h.redo.push(_clone(current))
  if (h.redo.length > MAX_DEPTH) h.redo.shift()
  _notify()
  return restore
}

/** Returns the snapshot to restore, or null. `current` goes back onto the undo stack. */
export function redo(id, current) {
  const h = _byId.get(id)
  if (!h || h.redo.length === 0) return null
  const restore = h.redo.pop()
  h.stack.push(_clone(current))
  if (h.stack.length > MAX_DEPTH) h.stack.shift()
  _notify()
  return restore
}

export function canUndo(id) { return (_byId.get(id)?.stack.length ?? 0) > 0 }
export function canRedo(id) { return (_byId.get(id)?.redo.length ?? 0) > 0 }

export function subscribe(fn) {
  _listeners.add(fn)
  return () => _listeners.delete(fn)
}

/**
 * Drop one subject's history — REQUIRED when the subject is swapped for a
 * different object (a new environment). The stack holds poses belonging to the
 * old splat; undoing into them would silently apply the previous environment's
 * placement to the new one.
 */
export function clear(id) {
  if (id == null) { _byId.clear(); _lastEditedId = null } else {
    _byId.delete(id)
    if (_lastEditedId === id) _lastEditedId = null
  }
  _notify()
}
